<template>
    <div class="p-8 flex flex-col min-h-[30rem]">
        <p class="text-body-4 font-normal text-default-1">
          {{ type === 'document' ? 'Set permissions' :  `Share your ${type}`}}</p>
        <div class="mt-4 w-full py-3.5 px-4 box-border border border-interactive-9"
            :class="{'cursor-pointer': !isOrganizationShareExpanded,
              'cursor-not-allowed pointer-events-none' : isMembersGroupsSelectDisabled}">
            <div class="w-full flex items-center justify-between cursor-pointer"
                @click="isOrganizationShareExpanded = !isOrganizationShareExpanded">
                <span class="text-body-6 text-default-1">Your organization</span>

                <img v-if="!isOrganizationShareExpanded && !isMembersGroupsSelectDisabled" src="@/assets/icons/plus.svg" alt="plus" class="w-4 h-4"/>
                <img v-if="isOrganizationShareExpanded" src="@/assets/icons/dash.svg" alt="plus" class="w-4 h-4"/>
            </div>
            <div v-if="isOrganizationShareExpanded" class="w-full mt-4">
                <ResourceSearchSelect
                    resource-name="members"
                    resource-identifier="uid"
                    :resource-label="(option) => option.first_name + ' ' + option.last_name"
                    :resource-callback="getMembers"
                    :default-options="selectedMembers.value.value.include"
                    :has-error="errors.selectedMembers !== null"
                    @select="handleOnSelectMembers"
                />
                <p v-show="errors.selectedMembers !== null" class="text-body-7 font-medium mt-1 text-interactive-3">
                    {{ errors.selectedMembers }}
                </p>
                <div class="mt-4">
                    <ResourceSearchSelect
                        resource-name="groups"
                        resource-identifier="uid"
                        resource-label="name"
                        :resource-callback="getGroups"
                        @select="handleOnSelectGroups"
                        :default-options="selectedGroups.value.value.include"
                        :has-error="errors.selectedGroups !== null"
                    />
                    <p v-show="errors.selectedGroups !== null" class="text-body-7 font-medium mt-1 text-interactive-3">
                        {{ errors.selectedGroups }}
                    </p>
                </div>
            </div>
        </div>

        <slot />

<!--        <div class="w-full mt-5" v-if="type === 'news'">-->
<!--            <Checkbox v-model="isPublic.value.value" label="Public on Organization’s Website"-->
<!--                      @update:model-value="handleGlobalOrPublicNews"/>-->
<!--        </div>-->
        <div class="w-full mt-5">
            <Checkbox v-if="isGlobalPostingActive" v-model="isGlobal.value.value" :label="`Global ${type}`"
                      @update:model-value="handleGlobalOrPublicNews"/>
        </div>
        <div class="flex flex-row mt-auto">
            <CoreButton
                    buttonType="primary"
                    size="default"
                    class="mt-4"
                    :disabled="!meta.valid || submitButtonLoading"
                    @click="handleOnSubmit"
                    :label="type === 'document' ? 'Save' : 'Share'"
                    :loading="submitButtonLoading"
            />
        </div>
    </div>
</template>

<script setup>
import * as yup from "yup";
import {useField, useForm} from "vee-validate";
import {computed, onMounted, ref} from "vue";
import Checkbox from "@/components/core/Checkbox.vue";
import CoreButton from "@/components/core/Button.vue";
import ResourceSearchSelect from "../../custom/ResourceSearchSelect.vue";
import {useMembers} from "@/composables/useMembers";
import {useGroups} from "@/composables/useGroups";
import {storeToRefs} from "pinia";
import {useAuth} from "@/stores/AuthStore";

const props = defineProps({
    members: {
        type: Array,
        default: () => [],
    },
    groups: {
        type: Array,
        default: () => [],
    },
    membersCount: {
        type: Number,
        default: 0,
    },
    groupsCount: {
        type: Number,
        default: 0,
    },
    isPublicShared: {
        type: Boolean,
        default: false,
    },
    isGlobalShared: {
        type: Boolean,
        default: false,
    },
    type: {
        type: String,
        required: true,
    },
    allowUnshare: {
        type: Boolean,
        default: false,
    }
});

const emit = defineEmits(['share']);

const submitButtonLoading = ref(false);
const isMembersGroupsSelectDisabled = ref(false);
const isOrganizationShareExpanded = ref(true);

const {organization} = storeToRefs(useAuth());

const {getMembers} = useMembers();
const {getGroups} = useGroups();

const validationSchema = yup.object().shape({
    selectedMembers: yup
        .object({
            include: yup.array().of(yup.string()),
            exclude: yup.array().of(yup.string()),
        })
        .test(
            "atLeastOneMember",
            "At least one member/group or option must be selected",
            function (value) {
                if(props.allowUnshare) {
                    return true;
                }
                const {selectedGroups} = this.parent;
                return hasAtLeastOne(value) || hasAtLeastOne(selectedGroups) || isGlobal.value.value;
            }
        )
        .label("The members"),
    selectedGroups: yup
        .object({
            include: yup.array().of(yup.string()),
            exclude: yup.array().of(yup.string()),
        })
        .test(
            "atLeastOneGroup",
            "At least one member/group or option must be selected",
            function (value) {
                if(props.allowUnshare) {
                    return true;
                }

                const {selectedMembers} = this.parent;
                return hasAtLeastOne(value) || hasAtLeastOne(selectedMembers) || isGlobal.value.value;
            }
        )
        .label("The groups"),
    isGlobal: yup.boolean().label("Global Resource"),
    isPublic: yup.boolean().label("Public on Organization’s Website"),
});

function hasAtLeastOne(groupsOrMembers) {
    return (
        (groupsOrMembers.include &&
            Array.isArray(groupsOrMembers.include) &&
            groupsOrMembers.include.length > 0) ||
        (groupsOrMembers.exclude &&
            Array.isArray(groupsOrMembers.exclude) &&
            groupsOrMembers.exclude.length > 0)
    );
}

const {meta, validate} = useForm({
    initialValues: {
        selectedMembers: {
            include: props.members,
            exclude: [],
        },
        selectedGroups: {
            include: props.groups,
            exclude: [],
        },
        isGlobal: props.isGlobalShared || false,
        isPublic: props.isPublicShared || false,
    },
    validationSchema
});

const selectedMembers = useField("selectedMembers");
const selectedGroups = useField("selectedGroups");
const isGlobal = useField("isGlobal");
const isPublic = useField("isPublic");

const errors = computed(() => {
    return {
        selectedMembers: selectedMembers.errorMessage.value ?? null,
        selectedGroups: selectedGroups.errorMessage.value ?? null,
        isGlobal: isGlobal.errorMessage.value ?? null,
        isPublic: isPublic.errorMessage.value ?? null,
    };
});

function handleOnSelectMembers(members) {
    selectedMembers.value.value = members;
}

function handleOnSelectGroups(groups) {
    selectedGroups.value.value = groups;
}

function handleGlobalOrPublicNews() {
    if (isGlobal.value.value || isPublic.value.value) {
        isOrganizationShareExpanded.value = false;
        isMembersGroupsSelectDisabled.value = true;
        selectedMembers.value.value = {include: ['*'], exclude: []};
        selectedGroups.value.value = {include: ['*'], exclude: []};
    } else {
        isOrganizationShareExpanded.value = true;
        isMembersGroupsSelectDisabled.value = false;
        selectedMembers.value.value = {include: [], exclude: []};
        selectedGroups.value.value = {include: [], exclude: []};
    }
}

async function handleOnSubmit() {
    await validate();

    if (!meta.value.valid) {
        return;
    }

    submitButtonLoading.value = true;

    const formData = {
        members: {include: selectedMembers.value.value.include, exclude: selectedMembers.value.value.exclude},
        groups: {include: selectedGroups.value.value.include, exclude: selectedGroups.value.value.exclude},
    };

    if(props.type !== 'documents') {
        formData.global = isGlobal.value.value;
    }

    if(props.type === 'news') {
        formData.public = props.isPublicShared;
        // formData.public = isPublic.value.value;
    }

    emit('share', formData);
}

const isGlobalPostingActive = computed(() => {
    if(!organization.value) {
        return false;
    }

    if(props.type === 'resource') {
        return organization.value.allow_global_resources;
    }
    return organization.value.allow_global_news;
});

onMounted(() => {
    isPublic.value.value = false;

    if (isGlobal.value.value || isPublic.value.value) {
        isOrganizationShareExpanded.value = false;
        isMembersGroupsSelectDisabled.value = true;
    }
});
</script>
